import { z } from 'zod'

import type { BatteryResource, ResourceConfig } from '@/features/controller/configuration/models/resourceConfigSchema'

/**
 * Minimal runtime validators for the LC-94 ↔ LC-191 seam.
 *
 * `resourceConfigSchema.ts` is auto-generated TS from `ResourceConfig.kt` and exposes only static
 * types — no Zod schemas. The LC-191 editor reads the resource.json document as `unknown` (parsed
 * JSON), so before handing an entry to the LC-94 `BindingPanel` we need a runtime structural check
 * that doesn't reproduce the whole tree's type.
 *
 * The schemas below cover only the fields BindingPanel + its mutation path read:
 *   - `resources: array` on the document
 *   - `id`, `type === 'battery'`, `binding.batteryRef` on the resource entry
 *
 * `.passthrough()` preserves the rest of the object so the spread-replace in
 * `useUpdateLocalControlConfigMutation` round-trips unknown keys back to disk untouched.
 */

const batterySteeringSchema = z.object({}).passthrough()

const batteryBindingSchema = z
  .object({
    batteryRef: z.string(),
  })
  .passthrough()

const batteryResourceEntrySchema = z
  .object({
    id: z.string(),
    type: z.literal('battery'),
    binding: batteryBindingSchema,
    steering: batterySteeringSchema.optional(),
  })
  .passthrough()

const resourceConfigDocumentSchema = z
  .object({
    resources: z.array(z.unknown()),
  })
  .passthrough()

export type BatteryResourceEntry = z.infer<typeof batteryResourceEntrySchema>

/** Validate that `entry` looks like a BatteryResource with the fields BindingPanel reads. */
export function parseBatteryResourceEntry(entry: unknown): BatteryResource | null {
  const result = batteryResourceEntrySchema.safeParse(entry)
  // Cast widens the validated subset back to the full BatteryResource type. Safe because the
  // additional fields BatteryResource declares are all optional, and `.passthrough()` keeps any
  // present-on-disk values verbatim for the mutation round-trip.
  return result.success ? (result.data as unknown as BatteryResource) : null
}

/** Validate that `document` looks like a ResourceConfig with at least a `resources[]`. */
export function parseResourceConfigDocument(document: unknown): ResourceConfig | null {
  const result = resourceConfigDocumentSchema.safeParse(document)
  return result.success ? (result.data as unknown as ResourceConfig) : null
}
